import React, { useState, useEffect } from 'react';
import { ExamQuestion, ExamSession } from '../types';
import { QuestionCard } from './QuestionCard';
import { ExamTimer } from './ExamTimer';
import { ExamOpticalForm } from './ExamOpticalForm';

interface ExamRunnerProps {
    exam: {
        id: string;
        name: string;
        type: 'TYT' | 'AYT' | 'YDT';
        duration: number; // in minutes
        questions: ExamQuestion[];
    };
    onFinish: (result: { answers: { [questionId: number]: string }, duration: number }) => void;
    onExit: () => void;
    mode?: 'take' | 'review';
    initialAnswers?: { [questionId: number]: string };
}

export const ExamRunner: React.FC<ExamRunnerProps> = ({ exam, onFinish, onExit, mode = 'take', initialAnswers }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<{ [questionId: number]: string }>(initialAnswers || {});
    const [bookmarks, setBookmarks] = useState<number[]>([]);
    const [isActive, setIsActive] = useState(true);
    const [showConfirmFinish, setShowConfirmFinish] = useState(false);
    const [startTime] = useState(Date.now());

    const isReview = mode === 'review';

    useEffect(() => {
        if (isReview) {
            setIsActive(false);
        }
    }, [isReview]);

    const currentQuestion = exam.questions[currentQuestionIndex];

    const handleAnswer = (optionLabel: string) => {
        if (isReview) return;
        setAnswers(prev => ({
            ...prev,
            [currentQuestion.id]: optionLabel
        }));
    };

    const handleBookmark = () => {
        if (isReview) return; // Bookmarking not allowed in review mode
        setBookmarks(prev => {
            if (prev.includes(currentQuestion.id)) {
                return prev.filter(id => id !== currentQuestion.id);
            }
            return [...prev, currentQuestion.id];
        });
    };

    const handleNext = () => {
        if (currentQuestionIndex < exam.questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const handleTimeUp = () => {
        setIsActive(false);
        alert('Süre doldu! Sınavınız otomatik olarak tamamlanıyor.');
        handleFinishExam();
    };

    const handleFinishExam = () => {
        setIsActive(false);
        // Calculate duration used in seconds
        const durationUsedSeconds = Math.floor((Date.now() - startTime) / 1000);
        onFinish({ answers, duration: durationUsedSeconds });
    };

    return (
        <div className="fixed inset-0 bg-gray-100 z-[200] flex flex-col h-screen w-screen overflow-hidden font-sans">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shadow-sm shrink-0 h-16">
                <div className="flex items-center gap-4">
                    <button onClick={onExit} className="text-gray-500 hover:text-gray-700 transition-colors" title="Çıkış">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    <div>
                        <h1 className="text-lg font-bold text-gray-800">{exam.name} {isReview && <span className="text-yellow-600 bg-yellow-100 px-2 rounded ml-2 text-xs">İNCELEME MODU</span>}</h1>
                        <span className="text-xs text-blue-600 font-bold bg-blue-50 px-2 py-0.5 rounded uppercase">{exam.type}</span>
                    </div>
                </div>

                <div className="flex items-center gap-6">
                    {!isReview && <ExamTimer duration={exam.duration} isActive={isActive} onTimeUp={handleTimeUp} />}
                    <button
                        onClick={() => isReview ? onExit() : setShowConfirmFinish(true)}
                        className={`${isReview ? 'bg-gray-600 hover:bg-gray-700' : 'bg-red-500 hover:bg-red-600'} text-white font-bold py-2 px-6 rounded-xl shadow transition-colors transform hover:scale-105`}
                    >
                        {isReview ? 'Kapat' : 'Sınavı Bitir'}
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden p-4 gap-4 max-w-[1920px] mx-auto w-full">
                {/* Left: Question Card */}
                <div className="flex-1 flex flex-col min-w-0">
                    <div className="flex-1 overflow-hidden">
                        <QuestionCard
                            question={currentQuestion}
                            selectedOption={answers[currentQuestion.id]}
                            onSelectOption={handleAnswer}
                            onBookmark={handleBookmark}
                            isBookmarked={bookmarks.includes(currentQuestion.id)}
                            showCorrectAnswer={isReview} // Pass this prop to QuestionCard
                        />
                    </div>

                    {/* Navigation */}
                    <div className="mt-4 flex justify-between items-center bg-white p-4 rounded-xl border border-gray-200 shadow-sm shrink-0">
                        <button
                            onClick={handlePrev}
                            disabled={currentQuestionIndex === 0}
                            className={`px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2 ${currentQuestionIndex === 0
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Önceki Soru
                        </button>

                        <div className="font-medium text-gray-500">
                            {currentQuestionIndex + 1} / {exam.questions.length}
                        </div>

                        <button
                            onClick={handleNext}
                            disabled={currentQuestionIndex === exam.questions.length - 1}
                            className={`px-6 py-3 rounded-lg font-bold transition-all flex items-center gap-2 ${currentQuestionIndex === exam.questions.length - 1
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                                }`}
                        >
                            Sonraki Soru
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Right: Optical Form - Hidden on mobile, typically */}
                <div className="w-80 hidden lg:block h-full shrink-0">
                    <ExamOpticalForm
                        questions={exam.questions}
                        currentQuestionIndex={currentQuestionIndex}
                        answers={answers}
                        bookmarks={bookmarks}
                        onQuestionSelect={setCurrentQuestionIndex}
                        isReview={isReview} // Pass review mode
                    />
                </div>
            </div>

            {/* Confirm Finish Modal */}
            {showConfirmFinish && (
                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl animate-scale-in text-center">
                        <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Sınavı Bitir?</h3>
                        <p className="text-gray-500 mb-6">
                            Sınavı bitirmek istediğinize emin misiniz? <br />
                            <span className="font-bold text-gray-700">
                                {Object.keys(answers).length} / {exam.questions.length}
                            </span> soruyu cevapladınız.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirmFinish(false)}
                                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors"
                            >
                                Vazgeç
                            </button>
                            <button
                                onClick={handleFinishExam}
                                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 shadow-lg transition-colors"
                            >
                                Evet, Bitir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
